import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import Swal from 'sweetalert2';
import { toast } from 'react-toastify';
import { Plus, Pencil, Trash2, X, Check, Loader2, GripVertical, ArrowUp, ArrowDown, Pill, FileAudio, Image as ImageIcon, AlertTriangle, RotateCw } from 'lucide-react';
import {
    getPrescriptionFields,
    createPrescriptionField,
    updatePrescriptionField,
    deletePrescriptionField,
    reorderPrescriptionFields,
} from '../../redux/prescriptionFieldSlice';
import { getDoctorSettings, updateDoctorSettings } from '../../redux/doctorSlice';
import { restrictnavigate, tryingnavigate, clearPendingNavigation } from '../../redux/blockingSlice';
import { serverReason } from '../patientTabs/prescriptionHelpers';

const sanitize = (val = '') => val.replace(/[<>]/g, '');

const STATIC_SECTIONS = [
    // Visible in dev/test. Kept commented on the preprod branch (medicines not enabled there).
    { key: 'medicines', icon: Pill, labelKey: 'static_medicines', fallback: 'Treatment Plan – Medicines' },
    { key: 'audio', icon: FileAudio, labelKey: 'static_audio', fallback: 'Upload Audio Recording' },
    { key: 'image', icon: ImageIcon, labelKey: 'static_image', fallback: 'Upload Image' },
];

const DEFAULT_VISIBILITY = { medicines: true, audio: true, image: true };

function PrescriptionFieldsTab() {
    const { t, i18n } = useTranslation(['prescriptionFields']);
    const lang = i18n?.language || 'en';
    const dispatch = useDispatch();
    const doctorId = localStorage.getItem('ID');
    const { fields, loading, loadError, submitting, deletingId } = useSelector((state) => state.prescriptionField);
    const { doctorSettings } = useSelector((state) => state.doctor);
    const { trynavigate } = useSelector((state) => state.blocking);

    const [newName, setNewName] = useState('');
    const [editId, setEditId] = useState(null);
    const [editName, setEditName] = useState('');
    const [togglingStatic, setTogglingStatic] = useState(null);
    const [draggedIndex, setDraggedIndex] = useState(null);
    const [dragOverIndex, setDragOverIndex] = useState(null);
    const isNavigatingRef = useRef(false);

    // Only two things in this tab are "unsaved": (1) text typed into the new-field
    // input that hasn't been submitted, and (2) an in-progress rename where the
    // edited name differs from the field's current value. Everything else
    // (toggles, reorder, delete) is auto-saved on the spot.
    const hasUnsavedChanges = () => {
        if (newName.trim().length > 0) return true;
        if (editId !== null) {
            const original = fields.find((f) => f.fieldId === editId)?.name || '';
            if (editName.trim() !== original.trim()) return true;
        }
        return false;
    };

    const visibility = useMemo(() => ({
        ...DEFAULT_VISIBILITY,
        ...(doctorSettings?.prescriptionSections || {}),
    }), [doctorSettings]);

    useEffect(() => {
        if (doctorId) {
            dispatch(getPrescriptionFields(doctorId));
            if (!doctorSettings || doctorSettings.doctorId !== doctorId) {
                dispatch(getDoctorSettings(doctorId));
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [doctorId, dispatch]);

    // Mirror the current dirty state into the blocking slice so the sidebar and
    // the parent DoctorSettingsPage know to intercept navigation/tab switches.
    useEffect(() => {
        if (isNavigatingRef.current) return;
        dispatch(restrictnavigate(hasUnsavedChanges()));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [newName, editId, editName, fields, dispatch]);

    // Clear the block when the tab unmounts so a different page doesn't inherit it.
    useEffect(() => {
        return () => {
            isNavigatingRef.current = false;
            dispatch(restrictnavigate(false));
        };
    }, [dispatch]);

    // Show the unsaved-changes confirmation when something tries to navigate.
    // On Discard we leave `pendingNavigation` intact so the sidebar /
    // DoctorSettingsPage consumer finishes the original navigation; on Stay we
    // drop it so a later attempt starts clean.
    useEffect(() => {
        if (trynavigate && hasUnsavedChanges() && !isNavigatingRef.current) {
            isNavigatingRef.current = true;
            Swal.fire({
                title: t('prescriptionFields.unsaved_changes_title', 'Unsaved Changes'),
                text: t('prescriptionFields.unsaved_changes_text', 'You have unsaved changes. Do you want to discard them?'),
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: t('prescriptionFields.discard', 'Discard'),
                cancelButtonText: t('prescriptionFields.stay', 'Stay'),
                confirmButtonColor: '#d33',
                cancelButtonColor: '#9ca3af',
                allowOutsideClick: false,
            }).then((result) => {
                if (result.isConfirmed) {
                    setNewName('');
                    setEditId(null);
                    setEditName('');
                    dispatch(restrictnavigate(false));
                    dispatch(tryingnavigate(false));
                } else {
                    isNavigatingRef.current = false;
                    dispatch(tryingnavigate(false));
                    dispatch(clearPendingNavigation());
                }
            });
        } else if (trynavigate) {
            dispatch(tryingnavigate(false));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [trynavigate]);

    const handleToggleStatic = async (sectionKey) => {
        if (togglingStatic) return;
        setTogglingStatic(sectionKey);
        try {
            const next = { ...visibility, [sectionKey]: !visibility[sectionKey] };
            const res = await dispatch(updateDoctorSettings({
                doctorId,
                prescriptionSections: next,
            }));
            if (!updateDoctorSettings.fulfilled.match(res)) {
                toast.error(serverReason(res.payload, t('prescriptionFields.update_failed', 'Failed to update field'), lang));
            }
        } finally {
            setTogglingStatic(null);
        }
    };

    const handleCreate = async (e) => {
        e?.preventDefault?.();
        const name = sanitize(newName).trim();
        if (!name) return;
        const res = await dispatch(createPrescriptionField({ doctorId, name }));
        if (createPrescriptionField.fulfilled.match(res)) {
            toast.success(t('prescriptionFields.created', 'Field added'));
            setNewName('');
        } else {
            toast.error(serverReason(res.payload, t('prescriptionFields.create_failed', 'Failed to add field'), lang));
        }
    };

    const startEdit = (field) => {
        setEditId(field.fieldId);
        setEditName(field.name);
    };

    const cancelEdit = () => {
        setEditId(null);
        setEditName('');
    };

    const saveEdit = async () => {
        const name = sanitize(editName).trim();
        if (!name) return;
        const res = await dispatch(updatePrescriptionField({
            fieldId: editId,
            payload: { name },
        }));
        if (updatePrescriptionField.fulfilled.match(res)) {
            toast.success(t('prescriptionFields.updated', 'Field updated'));
            cancelEdit();
        } else {
            toast.error(serverReason(res.payload, t('prescriptionFields.update_failed', 'Failed to update field'), lang));
        }
    };

    const handleToggleField = async (field) => {
        const res = await dispatch(updatePrescriptionField({
            fieldId: field.fieldId,
            payload: { visible: !field.visible },
        }));
        if (!updatePrescriptionField.fulfilled.match(res)) {
            toast.error(serverReason(res.payload, t('prescriptionFields.update_failed', 'Failed to update field'), lang));
        }
    };

    const handleDelete = async (field) => {
        const result = await Swal.fire({
            title: t('prescriptionFields.confirm_delete_title', 'Delete field?'),
            text: t('prescriptionFields.confirm_delete_text', 'Removing "{{name}}" will hide it from new appointments. Existing notes already saved against this field will remain in the database.', { name: field.name }),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#dc2626',
            cancelButtonColor: '#6b7280',
            confirmButtonText: t('prescriptionFields.delete_confirm', 'Yes, delete'),
            cancelButtonText: t('prescriptionFields.cancel', 'Cancel'),
        });
        if (!result.isConfirmed) return;
        const res = await dispatch(deletePrescriptionField(field.fieldId));
        if (deletePrescriptionField.fulfilled.match(res)) {
            toast.success(t('prescriptionFields.deleted', 'Field deleted'));
        } else {
            toast.error(serverReason(res.payload, t('prescriptionFields.delete_failed', 'Failed to delete field'), lang));
        }
    };

    // The slice moves the list at once and puts it back if the server refuses;
    // this says so, with the server's reason when it gave one.
    const saveOrder = async (order) => {
        const res = await dispatch(reorderPrescriptionFields({ doctorId, order }));
        if (reorderPrescriptionFields.fulfilled.match(res)) return;
        const reason = serverReason(res.payload, '', lang);
        toast.error(reason
            ? t('prescriptionFields.reorder_failed_reason', 'Could not save the new order ({{reason}}). The previous order is back.', { reason })
            : t('prescriptionFields.reorder_failed', 'Could not save the new order. The previous order is back.'));
    };

    const moveField = async (index, direction) => {
        const target = index + direction;
        if (target < 0 || target >= fields.length) return;
        const order = fields.map((f) => f.fieldId);
        [order[index], order[target]] = [order[target], order[index]];
        await saveOrder(order);
    };

    const handleDragStart = (e, index) => {
        setDraggedIndex(index);
        e.dataTransfer.effectAllowed = 'move';
        // Firefox needs data set or the drag never starts
        e.dataTransfer.setData('text/plain', String(index));
    };

    const handleDragOver = (e, index) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        if (index !== dragOverIndex) setDragOverIndex(index);
    };

    const handleDragLeave = (index) => {
        if (dragOverIndex === index) setDragOverIndex(null);
    };

    const handleDrop = async (e, index) => {
        e.preventDefault();
        const from = draggedIndex;
        setDraggedIndex(null);
        setDragOverIndex(null);
        if (from === null || from === index) return;
        const order = fields.map((f) => f.fieldId);
        const [moved] = order.splice(from, 1);
        order.splice(index, 0, moved);
        await saveOrder(order);
    };

    const handleDragEnd = () => {
        setDraggedIndex(null);
        setDragOverIndex(null);
    };

    return (
        <div data-testid="prescription-fields-tab" className="space-y-6">
            <div data-testid="prescription-fields-header">
                <h3 className="text-base font-semibold text-gray-800" data-testid="prescription-fields-heading">
                    {t('prescriptionFields.heading', 'Prescription Fields')}
                </h3>
                <p className="text-sm text-gray-500 mt-1" data-testid="prescription-fields-subheading">
                    {t('prescriptionFields.subheading', 'Choose which sections appear in the medical history of every appointment.')}
                </p>
            </div>

            {/* Built-in sections (hardcoded, persisted in doctorSettings.prescriptionSections) */}
            <div data-testid="prescription-fields-built-in-section">
                <h4 className="text-xs uppercase tracking-wider font-semibold text-gray-400 mb-2" data-testid="prescription-fields-built-in-heading">
                    {t('prescriptionFields.built_in_heading', 'Built-in sections')}
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="prescription-fields-built-in-list">
                    {STATIC_SECTIONS.map(({ key, icon: Icon, labelKey, fallback }) => {
                        const isOn = !!visibility[key];
                        const isBusy = togglingStatic === key;
                        return (
                            <label
                                key={key}
                                data-testid={`prescription-fields-static-${key}-row`}
                                className={`flex items-center gap-3 p-3 rounded-xl border bg-white shadow-sm cursor-pointer transition ${
                                    isOn
                                        ? 'border-gray-100 hover:border-[#4ABDBD]/40'
                                        : 'border-gray-100 bg-gray-50/60 opacity-70'
                                }`}
                            >
                                <input
                                    type="checkbox"
                                    checked={isOn}
                                    disabled={isBusy}
                                    onChange={() => handleToggleStatic(key)}
                                    className="w-4 h-4 accent-[#4ABDBD] cursor-pointer disabled:cursor-not-allowed"
                                    data-testid={`prescription-fields-static-${key}-checkbox`}
                                />
                                <Icon size={16} className={isOn ? 'text-[#4ABDBD]' : 'text-gray-400'} data-testid={`prescription-fields-static-${key}-icon`} />
                                <span className={`flex-1 text-sm font-medium ${isOn ? 'text-gray-800' : 'text-gray-500'}`} data-testid={`prescription-fields-static-${key}-label`}>
                                    {t(`prescriptionFields.${labelKey}`, fallback)}
                                </span>
                                {isBusy && <Loader2 size={12} className="animate-spin text-[#4ABDBD]" data-testid={`prescription-fields-static-${key}-loader`} />}
                            </label>
                        );
                    })}
                </div>
            </div>

            {/* Editable note fields */}
            <div data-testid="prescription-fields-notes-section">
                <h4 className="text-xs uppercase tracking-wider font-semibold text-gray-400 mb-2" data-testid="prescription-fields-notes-heading">
                    {t('prescriptionFields.notes_heading', 'Note fields')}
                </h4>

                <form onSubmit={handleCreate} className="flex items-center gap-2 mb-3" data-testid="prescription-fields-add-form">
                    <input
                        type="text"
                        value={newName}
                        onChange={(e) => setNewName(sanitize(e.target.value))}
                        maxLength={80}
                        placeholder={t('prescriptionFields.add_placeholder', 'New field name (e.g. Examination findings)')}
                        className="flex-1 px-3 py-2 rounded-xl border border-gray-200 focus:outline-none focus:border-[#4ABDBD] text-sm"
                        data-testid="prescription-fields-new-name-input"
                    />
                    <button
                        type="submit"
                        disabled={submitting || !newName.trim()}
                        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] text-white text-sm font-semibold shadow hover:shadow-md transition cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                        data-testid="prescription-fields-add-button"
                    >
                        {submitting ? <Loader2 size={14} className="animate-spin" data-testid="prescription-fields-add-loader" /> : <Plus size={14} data-testid="prescription-fields-add-icon" />}
                        {t('prescriptionFields.add_btn', 'Add Field')}
                    </button>
                </form>

                <div className="space-y-2" data-testid="prescription-fields-list">
                    {loading ? (
                        <div className="text-center text-gray-400 py-6" data-testid="prescription-fields-loading">
                            {t('prescriptionFields.loading', 'Loading...')}
                        </div>
                    ) : loadError ? (
                        <div role="alert" className="flex flex-col items-center gap-2 text-center py-6 px-4 rounded-xl border border-red-100 bg-red-50/60" data-testid="prescription-fields-load-error">
                            <p className="flex items-center gap-1.5 text-sm font-semibold text-red-700">
                                <AlertTriangle size={15} />
                                {t('prescriptionFields.load_failed', 'Your note fields could not be loaded.')}
                            </p>
                            <p className="text-xs text-red-600" data-testid="prescription-fields-load-error-reason">
                                {serverReason(loadError, t('prescriptionFields.load_failed_hint', 'Check your connection and try again.'), lang)}
                            </p>
                            <button
                                type="button"
                                onClick={() => dispatch(getPrescriptionFields(doctorId))}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-red-200 bg-white text-red-700 text-xs font-semibold hover:bg-red-50 transition cursor-pointer"
                                data-testid="prescription-fields-retry-button"
                            >
                                <RotateCw size={13} /> {t('prescriptionFields.retry', 'Retry')}
                            </button>
                        </div>
                    ) : fields.length === 0 ? (
                        <div className="text-center text-gray-400 py-6 bg-gray-50 rounded-xl" data-testid="prescription-fields-empty">
                            {t('prescriptionFields.empty', 'No fields yet — add one above.')}
                        </div>
                    ) : (
                        fields.map((f, idx) => {
                            const isEditing = editId === f.fieldId;
                            const isDeleting = deletingId === f.fieldId;
                            return (
                                <div
                                    key={f.fieldId}
                                    data-testid={`prescription-field-${f.fieldId}`}
                                    draggable={!isEditing}
                                    onDragStart={(e) => handleDragStart(e, idx)}
                                    onDragOver={(e) => handleDragOver(e, idx)}
                                    onDragLeave={() => handleDragLeave(idx)}
                                    onDrop={(e) => handleDrop(e, idx)}
                                    onDragEnd={handleDragEnd}
                                    className={`flex items-center gap-2 p-3 rounded-xl border bg-white shadow-sm transition ${
                                        f.visible
                                            ? 'border-gray-100 hover:border-[#4ABDBD]/40'
                                            : 'border-gray-100 bg-gray-50/60 opacity-70'
                                    } ${draggedIndex === idx ? 'opacity-40' : ''} ${dragOverIndex === idx && draggedIndex !== null && draggedIndex !== idx ? 'border-[#4ABDBD] ring-2 ring-[#4ABDBD]/30' : ''}`}
                                >
                                    <input
                                        type="checkbox"
                                        checked={!!f.visible}
                                        onChange={() => handleToggleField(f)}
                                        className="w-4 h-4 accent-[#4ABDBD] cursor-pointer"
                                        data-testid={`prescription-field-${f.fieldId}-visibility-checkbox`}
                                    />

                                    <span className="text-[10px] font-bold text-gray-400 w-6 text-center" data-testid={`prescription-field-${f.fieldId}-index`}>
                                        {String(idx + 1).padStart(2, '0')}
                                    </span>
                                    <GripVertical size={14} className={`text-gray-400 ${isEditing ? 'cursor-not-allowed' : 'cursor-grab active:cursor-grabbing'}`} data-testid={`prescription-field-${f.fieldId}-grip`} />

                                    {isEditing ? (
                                        <input
                                            type="text"
                                            value={editName}
                                            autoFocus
                                            maxLength={80}
                                            onChange={(e) => setEditName(sanitize(e.target.value))}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') saveEdit();
                                                if (e.key === 'Escape') cancelEdit();
                                            }}
                                            className="flex-1 px-2 py-1 rounded-lg border border-[#4ABDBD] focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]/30 text-sm bg-white"
                                            data-testid={`prescription-field-${f.fieldId}-edit-input`}
                                        />
                                    ) : (
                                        <span className={`flex-1 text-sm font-medium ${f.visible ? 'text-gray-800' : 'text-gray-500'}`} data-testid={`prescription-field-${f.fieldId}-name`}>
                                            {f.name}
                                        </span>
                                    )}

                                    {!isEditing && f.isSeeded && (
                                        <span className="text-[10px] uppercase tracking-wider font-semibold text-[#4ABDBD] bg-[#4ABDBD]/10 px-2 py-0.5 rounded-full" data-testid={`prescription-field-${f.fieldId}-default-badge`}>
                                            {t('prescriptionFields.default', 'Default')}
                                        </span>
                                    )}

                                    <div className="flex items-center gap-0.5" data-testid={`prescription-field-${f.fieldId}-actions`}>
                                        {isEditing ? (
                                            <>
                                                <button
                                                    type="button"
                                                    onClick={saveEdit}
                                                    disabled={submitting || !editName.trim()}
                                                    className="p-1.5 rounded-lg text-[#4ABDBD] hover:bg-[#4ABDBD]/10 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                                    aria-label={t('prescriptionFields.save', 'Save')}
                                                    data-testid={`prescription-field-${f.fieldId}-save-button`}
                                                >
                                                    {submitting ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={cancelEdit}
                                                    disabled={submitting}
                                                    className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                                    aria-label={t('prescriptionFields.cancel', 'Cancel')}
                                                    data-testid={`prescription-field-${f.fieldId}-cancel-button`}
                                                >
                                                    <X size={14} />
                                                </button>
                                            </>
                                        ) : (
                                            <>
                                                <button
                                                    type="button"
                                                    onClick={() => moveField(idx, -1)}
                                                    disabled={idx === 0}
                                                    className="p-1 rounded-md text-gray-400 hover:bg-gray-100 hover:text-gray-600 cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
                                                    aria-label={t('prescriptionFields.move_up', 'Move up')}
                                                    data-testid={`prescription-field-${f.fieldId}-move-up-button`}
                                                >
                                                    <ArrowUp size={13} />
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => moveField(idx, 1)}
                                                    disabled={idx === fields.length - 1}
                                                    className="p-1 rounded-md text-gray-400 hover:bg-gray-100 hover:text-gray-600 cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
                                                    aria-label={t('prescriptionFields.move_down', 'Move down')}
                                                    data-testid={`prescription-field-${f.fieldId}-move-down-button`}
                                                >
                                                    <ArrowDown size={13} />
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => startEdit(f)}
                                                    disabled={isDeleting}
                                                    className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-[#4ABDBD] cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                                                    aria-label={t('prescriptionFields.edit', 'Edit')}
                                                    data-testid={`prescription-field-${f.fieldId}-edit-button`}
                                                >
                                                    <Pencil size={14} />
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => handleDelete(f)}
                                                    disabled={isDeleting}
                                                    className="p-1.5 rounded-lg text-gray-500 hover:bg-red-50 hover:text-red-600 cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                                                    aria-label={t('prescriptionFields.delete', 'Delete')}
                                                    data-testid={`prescription-field-${f.fieldId}-delete-button`}
                                                >
                                                    {isDeleting ? (
                                                        <Loader2 size={14} className="animate-spin text-red-500" />
                                                    ) : (
                                                        <Trash2 size={14} />
                                                    )}
                                                </button>
                                            </>
                                        )}
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>
            </div>
        </div>
    );
}

export default PrescriptionFieldsTab;
